import { markRaw } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import {
  HireAvatarColumnFragment,
  HireAvatarColumnFragmentDoc,
  HireClassificationColumnFragment,
  HireClassificationColumnFragmentDoc,
  HireCreatedColumnFragment,
  HireCreatedColumnFragmentDoc,
  HireOrderByColumn,
  HireJobAndPoNumberColumnFragment,
  HireJobAndPoNumberColumnFragmentDoc,
  SortOrder,
  HireContractPopoverColumnFragment,
  HireContractPopoverColumnFragmentDoc,
  RateColumnHireFragmentDoc,
  RateColumnHireFragment,
  HireHasStaffingAgencyColumnFragmentDoc,
  HireHasStaffingAgencyColumnFragment,
  HirePaymentRequestColumnFragmentDoc,
  HirePaymentRequestColumnFragment,
} from '@/gql/graphql'
import HireAvatarColumn from '@/components/GraphqlDataTable/columns/Hire/HireAvatarColumn.vue'
import JobPoNumberColumn from '@/components/GraphqlDataTable/columns/Job/JobHirePoNumberColumn.vue'
import HireClassification from '@/components/GraphqlDataTable/columns/Hire/HireClassification.vue'
import HireCreatedColumn from '@/components/GraphqlDataTable/columns/Hire/HireCreatedColumn.vue'
import HireContractPopoverColumn from '@/components/GraphqlDataTable/columns/Hire/HireContractPopoverColumn.vue'
import RateColumn from '@/components/GraphqlDataTable/columns/RateColumn.vue'
import HireHasStaffingAgencyColumn from '@/components/GraphqlDataTable/columns/Hire/HireHasStaffingAgencyColumn.vue'
import HirePaymentRequestColumn from '@/components/GraphqlDataTable/columns/Hire/HirePaymentRequestColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'

const columns: ColumnMaps = {
  HireAvatarColumn: {
    fragment: HireAvatarColumnFragmentDoc,
    component: markRaw(HireAvatarColumn),
    columnDef: {
      title: $t('Worker'),
      key: 'HireAvatarColumnFragmentDoc',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireAvatarColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Worker Id',
        extraction: (row: HireAvatarColumnFragment) => `#${row.worker.id}`,
      },
      {
        title: 'Worker name',
        extraction: (row: HireAvatarColumnFragment) => row.worker.name,
      },
      {
        title: 'Worker email',
        extraction: (row: HireAvatarColumnFragment) => row.worker.email || '',
      },
      {
        title: 'Status',
        extraction: (row: HireAvatarColumnFragment) => `${row.activeStatus}`,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Worker name'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the worker name'),
      },
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the worker name'),
      },
    ],
  },
  HireClassificationColumn: {
    fragment: HireClassificationColumnFragmentDoc,
    component: markRaw(HireClassification),
    columnDef: {
      title: $t('Contract type & Classification'),
      key: 'classificationLabel',
      renderUsing: {
        component: markRaw(HireClassification),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Contract Type',
        extraction: (row: HireClassificationColumnFragment) =>
          `${row.contractType} - ${row.contractTypeLabel}`,
      },
      {
        title: 'Classification',
        extraction: (row: HireClassificationColumnFragment) =>
          `${row.classificationResult} - ${row.classificationLabel}`,
      },
    ],
  },
  HireCreatedColumn: {
    fragment: HireCreatedColumnFragmentDoc,
    component: markRaw(HireCreatedColumn),
    columnDef: {
      title: $t('Created'),
      key: 'createdAt',
      renderUsing: {
        component: markRaw(HireCreatedColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Created',
        extraction: (row: HireCreatedColumnFragment) => row.createdAt,
      },
    ],
  },
  HireJobAndPoNumberColumn: {
    fragment: HireJobAndPoNumberColumnFragmentDoc,
    component: markRaw(JobPoNumberColumn),
    columnDef: {
      title: $t('Hire & PO number'),
      key: 'job.id',
      orderAble: true,
      renderUsing: {
        component: markRaw(JobPoNumberColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: HireJobAndPoNumberColumnFragment) => row.job.name,
      },
      {
        title: 'Job url',
        extraction: (row: HireJobAndPoNumberColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.jobs.show, { job: row.job.id })}`,
      },
      {
        title: 'Purchase order',
        extraction: (row: HireJobAndPoNumberColumnFragment) => row.purchaseOrderNumber ?? '-',
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Job'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the job title'),
      },
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Job'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the job title'),
      },
    ],
  },
  HireContractPopoverColumn: {
    fragment: HireContractPopoverColumnFragmentDoc,
    component: markRaw(HireContractPopoverColumn),
    columnDef: {
      title: $t('Contract'),
      key: 'HireContractPopoverColumn',
      renderUsing: {
        component: markRaw(HireContractPopoverColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Contract',
        extraction: (row: HireContractPopoverColumnFragment) => `#${row.id}`,
      },
    ],
  },
  HirePaymentRequestColumn: {
    fragment: HirePaymentRequestColumnFragmentDoc,
    component: markRaw(HirePaymentRequestColumn),
    columnDef: {
      title: $t('Payment request'),
      key: 'HirePaymentRequestColumn',
      renderUsing: {
        component: markRaw(HirePaymentRequestColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Payment Requests',
        extraction: (row: HirePaymentRequestColumnFragment) => `#${row.id}`,
      },
    ],
  },
  HireHasStaffingAgencyColumn: {
    fragment: HireHasStaffingAgencyColumnFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Staffing Agency'),
      key: 'HireHasStaffingAgencyColumnFragmentDoc',
      renderUsing: {
        component: markRaw(HireHasStaffingAgencyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Staffing agency',
        extraction: (row: HireHasStaffingAgencyColumnFragment) =>
          `${!row.recruiterManagesWorkers ? '-' : row.recruiterOwnershipIsExpired ? 'Expired' : 'Active'}`,
      },
    ],
  },
  RateColumnHire: {
    fragment: RateColumnHireFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Rate'),
      key: 'rate',
      flexClass: { 'tw-float-right': true },
      renderUsing: {
        component: markRaw(RateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: RateColumnHireFragment) => `${row.rate}`,
      },
      {
        title: 'Rate type',
        extraction: (row: RateColumnHireFragment) => `${row.rateType}`,
      },
      {
        title: 'Currency',
        extraction: (row: RateColumnHireFragment) => `${row.currency}`,
      },
      {
        title: 'Has milestones',
        extraction: (row: RateColumnHireFragment) => `${row.hasMilestones}`,
      },
    ],
  },
}
export default columns
